'use client';

import {
  formatFileSize,
  toasterApi,
  type UploadingFile,
} from '@terra/ui-design-system';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  type ConversationAttachment,
  SESSION_ATTACHMENT_ACCEPT,
  SESSION_ATTACHMENT_MAX_IMAGE_FILE_SIZE_BYTES,
  isAllowedConversationAttachmentFile,
  isConversationAttachmentImageFile,
} from '@terra/shared-contracts-conversation-attachments';
import { useConversationAttachments } from '../../hooks/api/useConversationAttachments';
import { ChatAttachmentsPanel } from './ChatAttachmentsPanel';

type UploadingImage = {
  id: string;
  fileName: string;
  previewUrl: string;
  progress: number;
  status: 'uploading' | 'error';
};

type ChatAttachmentsControllerProps = {
  threadId: string;
  targetId?: string;
};

export type ChatAttachmentsController = {
  attachmentsPanel: React.ReactNode | null;
  fileUploadMenu: React.ReactNode;
  onFileUploadClick: () => void;
  uploadFiles: (files: File[]) => Promise<void>;
  imagePreviewUrls: Record<string, string>;
  isUploadingAttachments: boolean;
  hasPendingAttachments: boolean;
  peekPendingAttachments: () => ConversationAttachment[];
  hasUploadingAttachments: () => boolean;
  clearPendingAttachments: () => void;
  restorePendingAttachments: (attachments: ConversationAttachment[]) => void;
};

export const useChatAttachmentsController = ({
  threadId,
  targetId,
}: ChatAttachmentsControllerProps): ChatAttachmentsController => {
  const t = useTranslations('designSystem.chat.fileUpload');

  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const uploadingFilesRef = useRef<UploadingFile[]>([]);

  uploadingFilesRef.current = uploadingFiles;
  const [pendingAttachments, setPendingAttachments] = useState<
    ConversationAttachment[]
  >([]);
  const pendingAttachmentsRef = useRef<ConversationAttachment[]>([]);

  pendingAttachmentsRef.current = pendingAttachments;
  const [imagePreviewUrls, setImagePreviewUrls] = useState<
    Record<string, string>
  >({});
  const imagePreviewUrlsRef = useRef<Record<string, string>>({});

  imagePreviewUrlsRef.current = imagePreviewUrls;
  const [uploadingImages, setUploadingImages] = useState<UploadingImage[]>([]);
  const uploadingImagesRef = useRef<UploadingImage[]>([]);

  uploadingImagesRef.current = uploadingImages;
  const cancelledUploadIdsRef = useRef(new Set<string>());

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadAttachments, deleteAttachment, maxFiles, maxFileSizeBytes } =
    useConversationAttachments({ threadId });

  const onFileUploadClick = useCallback(() => {
    const input = fileInputRef.current;

    if (!input) {
      return;
    }

    input.accept = SESSION_ATTACHMENT_ACCEPT;
    input.click();
  }, []);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      const validatedFiles: File[] = [];

      for (const file of files) {
        if (!isAllowedConversationAttachmentFile(file)) {
          toasterApi.error({
            title: t('uploadError'),
            description: t('unsupportedFileType', { fileName: file.name }),
          });
          continue;
        }

        const maxAllowedBytes = isConversationAttachmentImageFile(file)
          ? SESSION_ATTACHMENT_MAX_IMAGE_FILE_SIZE_BYTES
          : maxFileSizeBytes;

        if (file.size > maxAllowedBytes) {
          toasterApi.error({
            title: t('uploadError'),
            description: t('fileTooLarge', {
              fileName: file.name,
              maxSize: formatFileSize(maxAllowedBytes),
            }),
          });
          continue;
        }

        validatedFiles.push(file);
      }

      if (validatedFiles.length === 0) {
        return;
      }

      const activeDocumentUploadCount = uploadingFilesRef.current.filter(
        (uploadingFile) => uploadingFile.status === 'uploading',
      ).length;
      const activeImageUploadCount = uploadingImagesRef.current.filter(
        (uploadingImage) => uploadingImage.status === 'uploading',
      ).length;
      const pendingAttachmentCount = pendingAttachmentsRef.current.length;

      if (
        pendingAttachmentCount +
          activeDocumentUploadCount +
          activeImageUploadCount +
          validatedFiles.length >
        maxFiles
      ) {
        toasterApi.error({
          title: t('uploadError'),
          description: t('attachmentLimitReached', { maxFiles }),
        });

        return;
      }

      const imageFiles = validatedFiles.filter((file) =>
        isConversationAttachmentImageFile(file),
      );
      const documentFiles = validatedFiles.filter(
        (file) => !isConversationAttachmentImageFile(file),
      );

      for (const file of imageFiles) {
        const fileId = `${Date.now()}-${file.name}-${Math.random()}`;
        const previewUrl = URL.createObjectURL(file);

        cancelledUploadIdsRef.current.delete(fileId);

        setUploadingImages((prev) => [
          ...prev,
          {
            id: fileId,
            fileName: file.name,
            previewUrl,
            progress: 0,
            status: 'uploading',
          },
        ]);

        try {
          const uploadedAttachments = await uploadAttachments({
            applicationId: targetId,
            files: [file],
            onUploadProgress: (progressEvent: {
              loaded: number;
              total?: number;
            }) => {
              const total = progressEvent.total ?? file.size;
              const percent = Math.round((progressEvent.loaded * 100) / total);

              setUploadingImages((prev) =>
                prev.map((uploadingImage) =>
                  uploadingImage.id === fileId
                    ? { ...uploadingImage, progress: percent }
                    : uploadingImage,
                ),
              );
            },
          });

          if (cancelledUploadIdsRef.current.has(fileId)) {
            cancelledUploadIdsRef.current.delete(fileId);
            await Promise.all(
              uploadedAttachments.map((attachment) =>
                deleteAttachment(attachment.id).catch((error: unknown) => {
                  console.error(
                    'Failed to delete cancelled image attachment:',
                    error,
                  );
                }),
              ),
            );
            URL.revokeObjectURL(previewUrl);
            continue;
          }

          setPendingAttachments((prev) => [...prev, ...uploadedAttachments]);
          setImagePreviewUrls((prev) => ({
            ...prev,
            ...Object.fromEntries(
              uploadedAttachments
                .filter((attachment) => attachment.fileType === 'IMAGE')
                .map((attachment) => [attachment.id, previewUrl]),
            ),
          }));
          setUploadingImages((prev) =>
            prev.filter((uploadingImage) => uploadingImage.id !== fileId),
          );
        } catch (error) {
          console.error('Failed to upload image:', error);
          if (cancelledUploadIdsRef.current.has(fileId)) {
            cancelledUploadIdsRef.current.delete(fileId);
            URL.revokeObjectURL(previewUrl);
            setUploadingImages((prev) =>
              prev.filter((uploadingImage) => uploadingImage.id !== fileId),
            );
            continue;
          }

          setUploadingImages((prev) =>
            prev.map((uploadingImage) =>
              uploadingImage.id === fileId
                ? { ...uploadingImage, status: 'error' }
                : uploadingImage,
            ),
          );
          toasterApi.error({
            title: t('uploadError'),
            description: file.name,
          });
        }
      }

      for (const file of documentFiles) {
        const fileId = `${Date.now()}-${file.name}`;

        setUploadingFiles((prev) => [
          ...prev,
          {
            id: fileId,
            name: file.name,
            size: file.size,
            progress: 0,
            status: 'uploading',
          },
        ]);

        try {
          const uploadedAttachments = await uploadAttachments({
            applicationId: targetId,
            files: [file],
            onUploadProgress: (progressEvent: {
              loaded: number;
              total?: number;
            }) => {
              const total = progressEvent.total ?? file.size;
              const percent = Math.round((progressEvent.loaded * 100) / total);

              setUploadingFiles((prev) =>
                prev.map((uf) =>
                  uf.id === fileId ? { ...uf, progress: percent } : uf,
                ),
              );
            },
          });

          setPendingAttachments((prev) => [...prev, ...uploadedAttachments]);
          setUploadingFiles((prev) =>
            prev.filter((uploadingFile) => uploadingFile.id !== fileId),
          );

          toasterApi.success({
            title: t('uploadSuccess'),
            description: file.name,
          });
        } catch (error) {
          console.error('Failed to upload file:', error);
          setUploadingFiles((prev) =>
            prev.map((uf) =>
              uf.id === fileId
                ? { ...uf, status: 'error', error: 'Upload failed' }
                : uf,
            ),
          );

          toasterApi.error({
            title: t('uploadError'),
            description: file.name,
          });
        }
      }
    },
    [
      deleteAttachment,
      targetId,
      maxFileSizeBytes,
      maxFiles,
      t,
      uploadAttachments,
    ],
  );

  const handleFileInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;

      if (!files || files.length === 0) {
        return;
      }

      const selectedFiles = Array.from(files);

      event.target.value = '';
      await uploadFiles(selectedFiles);
    },
    [uploadFiles],
  );

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadingFiles((prev) => prev.filter((uf) => uf.id !== fileId));
  }, []);

  const revokeImagePreviewUrl = useCallback((attachmentId: string) => {
    const previewUrl = imagePreviewUrlsRef.current[attachmentId];

    if (!previewUrl) {
      return;
    }

    URL.revokeObjectURL(previewUrl);
    setImagePreviewUrls((prev) => {
      const { [attachmentId]: _removed, ...rest } = prev;

      return rest;
    });
  }, []);

  const handleRemoveUploadingImage = useCallback((uploadId: string) => {
    cancelledUploadIdsRef.current.add(uploadId);
    setUploadingImages((prev) => {
      const uploadingImage = prev.find((image) => image.id === uploadId);

      if (uploadingImage?.status === 'error') {
        URL.revokeObjectURL(uploadingImage.previewUrl);
      }

      return prev.filter((image) => image.id !== uploadId);
    });
  }, []);

  const handleRemoveAttachment = useCallback(
    async (attachmentId: string) => {
      try {
        await deleteAttachment(attachmentId);
        setPendingAttachments((prev) =>
          prev.filter((attachment) => attachment.id !== attachmentId),
        );
        revokeImagePreviewUrl(attachmentId);
      } catch (error) {
        console.error('Failed to remove attachment:', error);
        toasterApi.error({
          title: t('removeAttachmentError'),
          description: pendingAttachmentsRef.current.find(
            (attachment) => attachment.id === attachmentId,
          )?.fileName,
        });
      }
    },
    [deleteAttachment, revokeImagePreviewUrl, t],
  );

  const peekPendingAttachments = useCallback(
    (): ConversationAttachment[] => pendingAttachmentsRef.current,
    [],
  );

  const isUploadingAttachments =
    uploadingImages.some(
      (uploadingImage) => uploadingImage.status === 'uploading',
    ) ||
    uploadingFiles.some(
      (uploadingFile) => uploadingFile.status === 'uploading',
    );

  const hasUploadingAttachments = useCallback(
    (): boolean =>
      uploadingImagesRef.current.some(
        (uploadingImage) => uploadingImage.status === 'uploading',
      ) ||
      uploadingFilesRef.current.some(
        (uploadingFile) => uploadingFile.status === 'uploading',
      ),
    [],
  );

  const clearPendingAttachments = useCallback(() => {
    setPendingAttachments([]);
  }, []);

  const restorePendingAttachments = useCallback(
    (attachments: ConversationAttachment[]) => {
      setPendingAttachments((prev) => [
        ...attachments,
        ...prev.filter(
          (existing) =>
            !attachments.some((restored) => restored.id === existing.id),
        ),
      ]);
    },
    [],
  );

  useEffect(
    () => () => {
      Object.values(imagePreviewUrlsRef.current).forEach((previewUrl) =>
        URL.revokeObjectURL(previewUrl),
      );
      uploadingImagesRef.current.forEach((uploadingImage) => {
        URL.revokeObjectURL(uploadingImage.previewUrl);
      });
    },
    [],
  );

  const attachmentsPanel =
    pendingAttachments.length > 0 ||
    uploadingImages.length > 0 ||
    uploadingFiles.length > 0 ? (
      <ChatAttachmentsPanel
        pendingAttachments={pendingAttachments}
        uploadingImages={uploadingImages}
        uploadingFiles={uploadingFiles}
        imagePreviewUrls={imagePreviewUrls}
        onRemoveUploadingImage={handleRemoveUploadingImage}
        onRemoveUploadingFile={handleRemoveFile}
        onRemoveAttachment={(attachmentId) => {
          void handleRemoveAttachment(attachmentId);
        }}
        removeFileAriaLabel={t('removeFile')}
      />
    ) : null;

  const fileUploadMenu = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      style={{ display: 'none' }}
      onChange={handleFileInputChange}
    />
  );

  return {
    attachmentsPanel,
    fileUploadMenu,
    onFileUploadClick,
    uploadFiles,
    imagePreviewUrls,
    isUploadingAttachments,
    hasPendingAttachments: pendingAttachments.length > 0,
    peekPendingAttachments,
    hasUploadingAttachments,
    clearPendingAttachments,
    restorePendingAttachments,
  };
};
